// Owner: engagements (owner feedback F19). Command bar (⌘K): "Log a call", "Add a note", "Write an email" (the quick actions,
// which ask for the record first), the Activities screen, its calendar and the activity types. "Book a meeting" and "New
// task" are the command bar items of scheduling and tasks. Loaded with the shell, so this file stays tiny and uses only the
// messages of `egx.*` (which load with the shell, too).
import { defineAppCommands, type CommandBarItem } from '../../extensions/commands';
import { quickActionsFor } from '../../extensions/quick-actions';
import { t } from '../../lib/i18n';
import { moduleOn } from '../../lib/modules';

const fold = (s: string) => s.toLocaleLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** The quick actions that scheduling and tasks already offer in the command bar themselves. */
const OWN_ITEMS = new Set(['engagements.meeting', 'engagements.task']);

export function engagementCommandItems(query: string): CommandBarItem[] {
  // An optional workspace module: nothing while Activities is off.
  if (!moduleOn('activities')) return [];
  const group = t('egx.command.group');
  const common = t('egx.command.keywords').split(' ');
  const items: CommandBarItem[] = [
    ...quickActionsFor({ placement: 'command' })
      .filter((a) => !OWN_ITEMS.has(a.id))
      .map<CommandBarItem>((a) => ({
        id: `${a.id}.new`,
        title: a.title(),
        icon: a.icon,
        group,
        keywords: [...(a.keywords?.() ?? []), ...common],
        ...(a.verbs ? { verbs: a.verbs() } : {}),
        run: () => void a.run({ placement: 'command' }),
        create: true,
        // The empty bar suggests them; `>` lists them (they are commands, too).
        suggest: true,
      })),
    {
      id: 'engagements.go',
      title: t('egx.command.all'),
      icon: 'pulse',
      group,
      keywords: common,
      href: '/engagements',
    },
    {
      id: 'engagements.calendar',
      title: t('egx.command.calendar'),
      icon: 'calendar',
      group,
      keywords: common,
      href: '/engagements?layout=calendar',
    },
    {
      id: 'engagements.types',
      title: t('egx.command.types'),
      icon: 'sliders',
      group,
      keywords: common,
      href: '/settings/engagement-types',
    },
  ];
  const words = fold(query).split(/\s+/).filter(Boolean);
  // Nothing typed: the bar picks (`suggest`), and `>` lists, what it wants of these.
  if (!words.length) return items;
  return items.filter((item) => {
    const haystack = fold([item.title, ...(item.keywords ?? [])].join(' '));
    return words.every((w) => haystack.includes(w));
  });
}

export default defineAppCommands({
  id: 'engagements',
  providers: [{ id: 'engagements.go', items: (query) => engagementCommandItems(query) }],
});
